import { FormApi, ValidationErrors } from 'final-form';
import arrayMutators from 'final-form-arrays';
import _capitalize from 'lodash.capitalize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import styled from 'styled-components';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../Button';
import { BaseModal } from '../modals/BaseModal';
import { useModalWizardNavigation } from './modalWizardNavigationContext';

const SAVE_TEXT = 'Save';
const NEXT_TEXT = 'Next';
const SAVING_TEXT = 'Saving...';

export type BaseModalProps = React.ComponentProps<typeof BaseModal>;

export interface ModalWizardProps<FormValues, InitialFormValues>
  extends Omit<BaseModalProps, 'open' | 'buttons'> {
  addArrayMutators?: boolean;
  completionButtonText?: string;
  nextButtonText?: string;
  nextButtonVariant?: 'contained' | 'outlined';
  saveButtonText?: string;
  initialValues?: InitialFormValues;
  isEditing: boolean;
  isNextButtonDisabled?: boolean;
  isSaveButtonDisabled?: boolean;
  /**
   * For parents that save outside of `handleSubmit` (see
   * `shouldBypassValidationOnNextPage` and `onSave`), where
   * final-form's own `submitting` never becomes true.
   */
  isSubmitting?: boolean;
  itemLabel: string;
  modalMaxWidth?: BaseModalProps['maxWidth'];
  onClose: () => void;
  onNextPage?: (values: Partial<FormValues>, pageNumber: number) => void;
  showSaveOnIntermediatePages?: boolean;
  onPageSubmit: (values: FormValues, errors: ValidationErrors) => Promise<void>;
  validateForm?: (values: FormValues) => ValidationErrors;
  shouldBypassValidationOnNextPage?: boolean;
  allowPristineSubmit?: boolean;
  parentHandlesSuccessAndError?: boolean;
  showBackButton?: boolean;
  startPage?: number;
  titleIcon?: React.ReactElement;
  wizardPages: React.ReactElement[];
  hidePageNumbers?: boolean;
  renderActionsLeft?: (pageNumber: number) => React.ReactElement;
  hideSaveButton?: boolean;
  shouldSaveButtonAppearAfterNext?: boolean;
  fullHeight?: boolean;
  modalId?: string;
}

interface ActionButtonsParams {
  disabled?: boolean;
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  onSaveClick?: React.MouseEventHandler<HTMLButtonElement>;
  isSubmitting: boolean;
}

const StyledPageNumberContainer = styled.div`
  position: relative;
  display: flex;
  width: fit-content;
`;

const StyledPageNumber = styled.div<{ $isActive: boolean }>`
  z-index: 1;
  padding: 0.2rem 0.45rem;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background-color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.green : theme.colors.graybbb};
  border-radius: 16px;
  transform: scale(0.8);

  &:not(:first-child) {
    margin-left: 2rem;
  }
`;

const StyledPageNumberConnector = styled.div`
  position: absolute;
  top: 11px;
  left: 3px;
  width: calc(100% - 6px);
  border-top: 1px solid ${({ theme }) => theme.colors.graybbb};
`;

export function ModalWizard<
  FormValues,
  InitialFormValues = Partial<FormValues>,
>({
  addArrayMutators = false,
  completionButtonText,
  nextButtonText,
  nextButtonVariant = 'contained',
  saveButtonText,
  initialValues,
  isEditing,
  isNextButtonDisabled,
  isSaveButtonDisabled,
  isSubmitting: isSubmittingFromParent,
  itemLabel,
  modalMaxWidth = 'md',
  onClose,
  onNextPage,
  onPageSubmit,
  showSaveOnIntermediatePages,
  validateForm,
  shouldBypassValidationOnNextPage,
  allowPristineSubmit,
  parentHandlesSuccessAndError,
  showBackButton,
  startPage = 0,
  titleIcon,
  wizardPages,
  hidePageNumbers: overrideHidePageNumbers,
  renderActionsLeft,
  hideSaveButton = false,
  shouldSaveButtonAppearAfterNext = false,
  fullHeight = false,
  modalId,
  ...baseModalProps
}: ModalWizardProps<FormValues, InitialFormValues>) {
  const { currentPageNumber, nextPage, previousPage, setPage } =
    useModalWizardNavigation();
  const isLastPage = currentPageNumber === wizardPages.length - 1;
  const startsOnLastPage = startPage === wizardPages.length - 1;
  const isFirstPage = currentPageNumber === 0;

  useEffect(() => {
    setPage(startPage);
  }, [startPage, setPage]);

  // Final-form resets its pristine status depending on which form fields are currently rendered,
  // this means that we can get pristine status only for the current modal page,
  // and have to track pristine statuses of the previous pages ourselves.
  const [previousPagePristineStatuses, setPreviousPagePristineStatuses] =
    useState(Array(wizardPages.length - 1).fill(true));
  const arePreviousPagesPristine = useMemo(
    () => previousPagePristineStatuses.every((pristine) => pristine),
    [previousPagePristineStatuses],
  );

  const { showUserFriendlyErrorToast, success: showSuccess } = useToast();

  const onSubmit = useCallback(
    async (
      values: FormValues,
      form: FormApi<FormValues, InitialFormValues>,
    ) => {
      if (!isLastPage) {
        const isCurrentPagePristine = form.getState().pristine;
        const newPristineStatuses = [...previousPagePristineStatuses];
        newPristineStatuses[currentPageNumber] = isCurrentPagePristine;
        setPreviousPagePristineStatuses(newPristineStatuses);

        onNextPage && onNextPage(values, currentPageNumber);
        nextPage();
        return;
      }
      const { errors } = form.getState();
      if (parentHandlesSuccessAndError) {
        await onPageSubmit(values, errors);
      } else {
        try {
          await onPageSubmit(values, errors);
          showSuccess(
            `${_capitalize(itemLabel)} has been ${
              isEditing ? 'edited.' : 'created.'
            }`,
          );
          onClose();
        } catch (error) {
          showUserFriendlyErrorToast(error, `Unable to save ${itemLabel}.`, {
            dontAutoHide: true,
          });
        }
      }
    },
    [
      nextPage,
      isEditing,
      isLastPage,
      itemLabel,
      onClose,
      onNextPage,
      onPageSubmit,
      parentHandlesSuccessAndError,
      previousPagePristineStatuses,
      showSuccess,
      showUserFriendlyErrorToast,
      currentPageNumber,
    ],
  );

  const getButtons = useCallback<
    (params: ActionButtonsParams) => React.ReactElement
  >(
    ({ disabled = false, onClick, onSaveClick, isSubmitting }) => {
      const getSaveButtonText = () => {
        if (isSubmitting) return SAVING_TEXT;
        return isLastPage
          ? completionButtonText ?? SAVE_TEXT
          : nextButtonText ?? NEXT_TEXT;
      };

      const saveButtonId =
        getSaveButtonText() === SAVE_TEXT
          ? `${modalId}-save-button`
          : undefined;

      const saveButton =
        !isLastPage && !hideSaveButton && showSaveOnIntermediatePages ? (
          <Button
            color="primary"
            disabled={isSaveButtonDisabled || isSubmitting}
            onClick={onSaveClick}
            type="button"
            variant="contained"
          >
            {isSubmitting ? SAVING_TEXT : saveButtonText ?? SAVE_TEXT}
          </Button>
        ) : null;

      const nextOrSubmitButton = (!isLastPage ||
        (isLastPage && !hideSaveButton)) && (
        <Button
          color="primary"
          disabled={disabled}
          onClick={onClick}
          type="submit"
          variant={isLastPage ? 'contained' : nextButtonVariant}
          id={saveButtonId}
        >
          {getSaveButtonText()}
        </Button>
      );

      return (
        <>
          {!isFirstPage && showBackButton && (
            <Button color="primary" onClick={previousPage} type="button">
              Back
            </Button>
          )}
          {shouldSaveButtonAppearAfterNext ? (
            <>
              {nextOrSubmitButton}
              {saveButton}
            </>
          ) : (
            <>
              {saveButton}
              {nextOrSubmitButton}
            </>
          )}
        </>
      );
    },
    [
      modalId,
      isLastPage,
      hideSaveButton,
      showSaveOnIntermediatePages,
      isSaveButtonDisabled,
      saveButtonText,
      nextButtonVariant,
      isFirstPage,
      showBackButton,
      previousPage,
      shouldSaveButtonAppearAfterNext,
      completionButtonText,
      nextButtonText,
    ],
  );

  const hidePageNumbers = useMemo(
    () =>
      overrideHidePageNumbers ||
      wizardPages.length === 1 ||
      (startsOnLastPage && !showBackButton),
    [
      overrideHidePageNumbers,
      showBackButton,
      startsOnLastPage,
      wizardPages.length,
    ],
  );

  const actionsLeft = useMemo(() => {
    if (renderActionsLeft) {
      return renderActionsLeft(currentPageNumber);
    }
    return hidePageNumbers ? undefined : (
      <StyledPageNumberContainer>
        {wizardPages.map((_, index) => (
          <StyledPageNumber
            key={`page-number-${index + 1}`}
            $isActive={index === currentPageNumber}
          >
            {index + 1}
          </StyledPageNumber>
        ))}
        <StyledPageNumberConnector />
      </StyledPageNumberContainer>
    );
  }, [hidePageNumbers, renderActionsLeft, currentPageNumber, wizardPages]);

  return (
    <Form<FormValues, InitialFormValues>
      initialValues={initialValues}
      // @ts-expect-error - arrayMutators from final-form-arrays has `any` in its type, and our form expects `FormValues`
      mutators={addArrayMutators ? { ...arrayMutators } : undefined}
      onSubmit={onSubmit}
      validate={validateForm}
      // Not subscribing to `values` because it re-renders the wizard on each
      // keystroke. Read values from the form API at next & submit time instead
      subscription={{
        pristine: true,
        submitting: true,
      }}
    >
      {({ handleSubmit, pristine, submitting, form }) => {
        const isSubmitting = submitting || !!isSubmittingFromParent;

        return (
          <form onSubmit={handleSubmit}>
            <BaseModal
              buttons={getButtons({
                disabled:
                  (!isLastPage && isNextButtonDisabled) ||
                  (isLastPage &&
                    (isSaveButtonDisabled ||
                      (pristine &&
                        arePreviousPagesPristine &&
                        !allowPristineSubmit))) ||
                  isSubmitting,
                isSubmitting,
                onClick: (e) => {
                  if (shouldBypassValidationOnNextPage) {
                    e.preventDefault();
                    return onSubmit(
                      form.getState().values,
                      form as FormApi<FormValues, InitialFormValues>,
                    );
                  }
                  return handleSubmit(e);
                },
                onSaveClick: async (e) => {
                  e.preventDefault();
                  const { values, errors } = form.getState();
                  await onPageSubmit?.(values, errors);
                },
              })}
              actionsLeft={actionsLeft}
              icon={titleIcon}
              id={modalId}
              maxWidth={modalMaxWidth}
              fullHeight={fullHeight}
              onClose={onClose}
              open
              showCloseButton
              {...baseModalProps}
            >
              {wizardPages[currentPageNumber]}
            </BaseModal>
          </form>
        );
      }}
    </Form>
  );
}
